/**
 * EvidenceUploads — reusable evidence-document attacher.
 *
 * Drops onto any record (equipment service log, RSO appointment letter, EPA
 * licence, staff licence, lead apron test, TLD reading, practice cert) and
 * provides:
 *   • drag-and-drop (or click-to-pick) file uploader for PDF/JPG/PNG ≤ 10MB
 *   • list of attached files with download + delete (admin/PM only)
 *
 * Backed by /api/uploads which keys files by parent_type + parent_id.
 */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Paperclip, Upload, FileText, Image as ImageIcon, Trash2, Loader2, Download } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const C = '#42CDD7';
const MAX_BYTES = 10 * 1024 * 1024;

function fmtBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function EvidenceUploads({ parentType, parentId, label = 'Evidence', testidPrefix = 'evidence' }) {
  const { user } = useAuth();
  const isPM = user?.role === 'admin' || user?.role === 'superuser';
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    if (!parentId) { setFiles([]); return; }
    try {
      const r = await axios.get(`${API}/uploads`, {
        params: { parent_type: parentType, parent_id: parentId },
        withCredentials: true,
      });
      setFiles(r.data || []);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load uploads.');
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [parentType, parentId]);

  const onUpload = async (file) => {
    if (!file) return;
    setErr(null);
    if (file.size > MAX_BYTES) { setErr(`File exceeds 10 MB.`); return; }
    if (!['application/pdf', 'image/jpeg', 'image/png'].includes(file.type)) {
      setErr('Only PDF / JPG / PNG accepted.'); return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('parent_type', parentType);
      fd.append('parent_id', parentId);
      fd.append('label', label);
      await axios.post(`${API}/uploads`, fd, { withCredentials: true });
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Upload failed.');
    }
    setBusy(false);
  };

  const onDelete = async (id) => {
    if (!window.confirm('Delete this evidence file?')) return;
    try {
      await axios.delete(`${API}/uploads/${id}`, { withCredentials: true });
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Delete failed.');
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f) onUpload(f);
  };

  if (!parentId) {
    return (
      <p className="text-[11px] text-muted-foreground italic" data-testid={`${testidPrefix}-pending`}>
        Save the record first to attach evidence files.
      </p>
    );
  }

  return (
    <div className="space-y-2" data-testid={`${testidPrefix}-block`}>
      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
        <Paperclip className="w-3 h-3" />
        <span>Evidence ({files.length})</span>
        {err && <span className="text-red-500 normal-case font-medium tracking-normal ml-2">{err}</span>}
      </div>

      {isPM && (
        <div
          onDragOver={e => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          data-testid={`${testidPrefix}-drop`}
          className={`cursor-pointer rounded-lg border-2 border-dashed p-3 text-center text-xs transition ${
            drag ? 'border-[#42CDD7] bg-[#42CDD710]' : 'border-border hover:border-[#42CDD7]/50'
          }`}
          style={{ color: C }}
        >
          {busy ? (
            <span className="inline-flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…</span>
          ) : (
            <span className="inline-flex items-center gap-1.5"><Upload className="w-3.5 h-3.5" /> Drop PDF/JPG/PNG or click to upload (≤10 MB)</span>
          )}
          <input ref={inputRef} type="file" accept=".pdf,application/pdf,image/jpeg,image/png"
            onChange={e => onUpload(e.target.files?.[0])} className="hidden"
            data-testid={`${testidPrefix}-input`} />
        </div>
      )}

      {files.length > 0 && (
        <ul className="space-y-1" data-testid={`${testidPrefix}-list`}>
          {files.map(f => (
            <li key={f.id} className="flex items-center gap-2 text-xs bg-muted/40 border border-border rounded-md px-2 py-1.5"
              data-testid={`${testidPrefix}-item-${f.id}`}>
              {f.content_type === 'application/pdf'
                ? <FileText className="w-3.5 h-3.5 shrink-0" style={{ color: C }} />
                : <ImageIcon className="w-3.5 h-3.5 shrink-0" style={{ color: C }} />}
              <a href={`${API}/uploads/${f.id}`} target="_blank" rel="noreferrer"
                className="flex-1 min-w-0 truncate font-medium hover:underline text-foreground"
                title={f.filename}>{f.filename}</a>
              <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">{fmtBytes(f.size)}</span>
              <a href={`${API}/uploads/${f.id}`} download={f.filename}
                className="text-muted-foreground hover:text-foreground"
                data-testid={`${testidPrefix}-download-${f.id}`} title="Download">
                <Download className="w-3.5 h-3.5" />
              </a>
              {isPM && (
                <button onClick={() => onDelete(f.id)}
                  className="text-muted-foreground hover:text-red-500"
                  data-testid={`${testidPrefix}-delete-${f.id}`} title="Delete">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
